import { UserRole } from '@shared/types';
import jwtDecode from 'jwt-decode';

/**
 * 로그인 후 "이 계정은 이 포털밖에 볼 게 없다"가 확정되는 경우에만 목적지를 갈아끼운다.
 *
 * - roles: 이 포털을 여는 role (하나라도 있으면 해당)
 * - home: 갈아끼울 목적지
 */
interface ExclusivePortal {
  roles: UserRole[];
  home: string;
}

const EXCLUSIVE_PORTALS: ExclusivePortal[] = [
  { roles: [UserRole.GiftAgent], home: '/gift-seller' },
  {
    roles: [UserRole.StoreSeller, UserRole.StoreSellerAdmin, UserRole.StoreSellerWorker],
    home: '/flower-seller',
  },
  //* 답례품 공급업체(배송관리자)
  { roles: [UserRole.GiftBrandSeller], home: '/gift-brand' },
];

/**
 * 포털을 여는 모든 role.
 * 고객·게스트 role(BugoCustomer/BugoGuest/StoreCustomer/StoreGuest)은 누구나 달고
 * 있을 수 있어서 판단에서 제외한다.
 */
const PORTAL_ROLES: UserRole[] = [
  UserRole.Admin,
  UserRole.BugoAgencyAdmin,
  UserRole.BugoAgencyBranchAdmin,
  UserRole.BugoAgencyWorker,
  UserRole.BugoFuneralHome,
  ...EXCLUSIVE_PORTALS.flatMap((portal) => portal.roles),
];

//* JWT에서 roles 추출 (실패해도 로그인 흐름을 막지 않는다)
export const getRolesFromToken = (token?: string | null): UserRole[] => {
  if (!token) return [];
  try {
    const { roles } = jwtDecode(token) as { roles?: UserRole[] };
    return roles ?? [];
  } catch (e) {
    return [];
  }
};

//* 해당 포털 role만 가지고 다른 포털 role은 없는 계정인지
const isExclusiveTo = (portal: ExclusivePortal, roles: UserRole[]): boolean =>
  roles.some((role) => portal.roles.includes(role)) &&
  !roles.some((role) => PORTAL_ROLES.includes(role) && !portal.roles.includes(role));

/**
 * 갈 곳이 정해지지 않은 로그인인지 — 메인 라우터("/")로 들어온 경우.
 *
 * 목적지가 구체적으로 정해진 로그인(결제 도중 로그인해서 체크아웃으로 돌아가야 하거나,
 * 특정 포털 로그인 페이지로 들어온 경우)은 절대 가로채면 안 된다. 판매자 계정이
 * 화환을 직접 구매하다 로그인하면 결제 흐름으로 돌아가야 하는 것처럼.
 */
const isUnspecifiedDestination = (redirectUrl: string): boolean =>
  (redirectUrl.split('?')[0] ?? '').replace(/\/+$/, '') === '';

/**
 * 로그인 후 이동할 경로.
 * 메인("/")으로 로그인한 단일 포털 계정(GiftAgent·화환 판매자·답례품 공급업체 단독)은
 * 그 홈에서 볼 게 없으므로 각자의 포털 홈으로 보낸다.
 * 목적지가 이미 정해진 로그인은 원래 redirectUrl 그대로 둔다.
 */
export const resolveLoginRedirectUrl = (
  token: string | null | undefined,
  redirectUrl: string,
): string => {
  if (!isUnspecifiedDestination(redirectUrl)) return redirectUrl;

  const roles = getRolesFromToken(token);
  const portal = EXCLUSIVE_PORTALS.find((candidate) => isExclusiveTo(candidate, roles));
  return portal ? portal.home : redirectUrl;
};
